/* Cart drawer — Residence Membership toggle (cart drawer + cart page) */

const MEMBERSHIP_MIN_CART_TOTAL = 125000;
const MEMBERSHIP_TOAST_DURATION_MS = 5000;
const MEMBERSHIP_TOAST_TRANSITION_MS = 300;
let membershipBootTimer;
let membershipToastHideTimer;
let lastKnownMembershipSubtotal = null;
let membershipSubtotalInitialized = false;

function isCustomerMember() {
  return document.body?.dataset.isMember === 'true';
}

function isMembershipUpsellEnabled() {
  return document.body?.dataset.showMemberProduct === 'true';
}

/**
 * Either membership product may be the cart line: carts created before the 2026
 * product went live still hold the legacy one. Matching only the current id
 * would leave the toggle reading "off" beside a membership already in the cart,
 * letting the shopper add a second one.
 */
function getMembershipProductIds() {
  const body = document.body;
  if (!body) return [];
  const raw = body.dataset.membershipProductIds || body.dataset.membershipProductId || '';
  return raw
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((value) => value && !Number.isNaN(value));
}

function hideMembershipMounts() {
  ['cart-drawer-membership-mount', 'cart-page-membership-mount'].forEach((id) => {
    const mount = document.getElementById(id);
    if (mount) mount.classList.add('hidden');
  });
}

function getMembershipUpsell(scope = document) {
  const root = scope && scope.querySelector ? scope : document;
  return root.querySelector('[data-membership-upsell]');
}

function isMembershipLine(line, variantId) {
  if (!line) return false;
  if (getMembershipProductIds().includes(line.product_id)) return true;
  if (line.variant_id === variantId || line.id === variantId) return true;
  return false;
}

async function fetchCartJson() {
  const response = await fetch(`/cart.js?timestamp=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to load cart');
  return response.json();
}

function syncMembershipActiveState(upsell, toggle) {
  if (!toggle) return;
  const isActive = toggle.getAttribute('aria-checked') === 'true';
  toggle.classList.toggle('is-active', isActive);
  if (upsell) upsell.classList.toggle('is-active', isActive);
}

function setMembershipToggleChecked(upsell, toggle, checked) {
  if (!toggle) return;
  toggle.setAttribute('aria-checked', checked ? 'true' : 'false');
  syncMembershipActiveState(upsell, toggle);
}

async function syncMembershipToggleState(upsell) {
  const variantId = parseInt(upsell.dataset.variantId, 10);
  const toggle = upsell.querySelector('[data-membership-toggle]');
  if (!toggle || Number.isNaN(variantId)) return;

  try {
    const cart = await fetchCartJson();
    if (isCartDrawerMembershipUpsell(upsell) && !shouldShowMembershipUpsell(cart)) {
      setMembershipToggleChecked(upsell, toggle, false);
      return;
    }
    const inCart = cart.items.some((item) => isMembershipLine(item, variantId));
    setMembershipToggleChecked(upsell, toggle, inCart);
  } catch (error) {
    console.error('Membership sync error:', error);
    syncMembershipActiveState(upsell, toggle);
  }
}

function getMembershipSellingPlanId(upsell) {
  const id = parseInt(upsell?.dataset.sellingPlanId, 10);
  return Number.isNaN(id) ? null : id;
}

async function addMembershipToCart(variantId, sellingPlanId) {
  // The plan is what makes this a subscription. Added without one the membership
  // bills once, no contract is created, and Flow never grants the member tag, so
  // the customer would pay and receive nothing. Fail instead of charging.
  if (!sellingPlanId) {
    throw new Error('Membership variant has no selling plan; refusing one-time add');
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1, selling_plan: sellingPlanId }]
    })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.description || data.message || 'Unable to add membership');
  }
}

async function removeMembershipFromCart(variantId) {
  const cart = await fetchCartJson();
  // Every membership line, not just the first: a cart holding both the legacy and
  // the 2026 product would otherwise keep one and flip the toggle straight back on.
  const items = cart.items.filter((line) => isMembershipLine(line, variantId));
  if (!items.length) return;

  for (const item of items) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Unable to remove membership');
    }
  }
}

async function waitForMembershipCartState(variantId, expectedInCart, attempts = 12) {
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    const cart = await fetchCartJson();
    const inCart = cart.items.some((item) => isMembershipLine(item, variantId));
    if (inCart === expectedInCart) return true;
  }
  return false;
}

function refreshCartAfterUpsellChange() {
  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

  const cartItems = document.getElementById('cart-items');
  if (cartItems && typeof cartItems.refresh === 'function') {
    cartItems.refresh();
  }
}

function getCartDrawerCheckoutButton() {
  return document.querySelector('.cart-drawer__checkout-buttons .btn--primary');
}

function setCartDrawerCheckoutLoading(isLoading) {
  const checkoutBtn = getCartDrawerCheckoutButton();
  if (!checkoutBtn) return;
  checkoutBtn.classList.toggle('is-loading', isLoading);
}

async function handleMembershipToggle(toggle) {
  const upsell = toggle.closest('[data-membership-upsell]');
  if (!upsell || toggle.disabled) return;

  if (isCustomerMember()) return;

  const checkoutBtn = getCartDrawerCheckoutButton();
  if (checkoutBtn?.classList.contains('is-loading')) return;

  const variantId = parseInt(upsell.dataset.variantId, 10);
  if (Number.isNaN(variantId)) return;

  const shouldRemove = toggle.getAttribute('aria-checked') === 'true';

  if (!shouldRemove && isCartDrawerMembershipUpsell(upsell)) {
    const cart = await fetchCartJson();
    if (!shouldShowMembershipUpsell(cart)) {
      setMembershipToggleChecked(upsell, toggle, false);
      return;
    }
  }

  setMembershipToggleChecked(upsell, toggle, !shouldRemove);
  toggle.disabled = true;
  setCartDrawerCheckoutLoading(true);

  try {
    if (shouldRemove) {
      await removeMembershipFromCart(variantId);
      await waitForMembershipCartState(variantId, false);
    } else {
      await addMembershipToCart(variantId, getMembershipSellingPlanId(upsell));
      await waitForMembershipCartState(variantId, true);
      if (typeof window.syncMembershipWelcome === 'function') {
        await window.syncMembershipWelcome();
      }
    }

    refreshCartAfterUpsellChange();
    scheduleMembershipBoot();
  } catch (error) {
    console.error('Membership toggle error:', error);
    await syncMembershipToggleState(upsell);
  } finally {
    toggle.disabled = false;
    setCartDrawerCheckoutLoading(false);
  }
}

function initCartDrawerMembership(scope = document) {
  const root = scope && scope.querySelector ? scope : document;
  const upsells = root.querySelectorAll('[data-membership-upsell]');
  if (!upsells.length) return;

  upsells.forEach((upsell) => {
    const toggle = upsell.querySelector('[data-membership-toggle]');
    if (toggle) syncMembershipActiveState(upsell, toggle);
    syncMembershipToggleState(upsell);
  });
}

function cartHasNonMembershipItems(cart) {
  return cart.items.some((item) => !isMembershipLine(item));
}

function getMembershipMinimumCents() {
  // The upsell section carries the threshold on both the drawer and the cart
  // page; #cart-drawer only exists on the drawer, so it is the fallback.
  const sources = [
    document.querySelector('[data-membership-upsell]'),
    document.getElementById('cart-drawer'),
  ];

  for (const element of sources) {
    const value = element && parseInt(element.dataset.membershipMinimum, 10);
    if (value && !Number.isNaN(value) && value > 0) return value;
  }

  return MEMBERSHIP_MIN_CART_TOTAL;
}

function getMembershipQualifyingSubtotal(cart) {
  if (!cart || !Array.isArray(cart.items)) return 0;

  return cart.items.reduce((sum, item) => {
    if (isMembershipLine(item)) return sum;
    return sum + (item.original_line_price || 0);
  }, 0);
}

function isMembershipToastEnabled() {
  if (isCustomerMember()) return false;
  if (!isMembershipUpsellEnabled()) return false;
  if (!document.getElementById('cart-drawer-membership-toast')) return false;
  return Boolean(document.getElementById('cart-drawer'));
}

function showCartDrawerMembershipToast() {
  const toast = document.getElementById('cart-drawer-membership-toast');
  if (!toast) return;

  clearTimeout(membershipToastHideTimer);

  if (toast.classList.contains('is-visible')) {
    membershipToastHideTimer = setTimeout(() => {
      hideCartDrawerMembershipToast();
    }, MEMBERSHIP_TOAST_DURATION_MS);
    return;
  }

  toast.classList.remove('is-visible');
  toast.setAttribute('aria-hidden', 'true');
  void toast.offsetHeight;

  requestAnimationFrame(() => {
    toast.classList.add('is-visible');
    toast.setAttribute('aria-hidden', 'false');
  });

  membershipToastHideTimer = setTimeout(() => {
    hideCartDrawerMembershipToast();
  }, MEMBERSHIP_TOAST_DURATION_MS);
}

function hideCartDrawerMembershipToast() {
  const toast = document.getElementById('cart-drawer-membership-toast');
  if (!toast || !toast.classList.contains('is-visible')) return;

  clearTimeout(membershipToastHideTimer);
  toast.classList.remove('is-visible');

  setTimeout(() => {
    if (!toast.classList.contains('is-visible')) {
      toast.setAttribute('aria-hidden', 'true');
    }
  }, MEMBERSHIP_TOAST_TRANSITION_MS);
}

function evaluateMembershipThresholdToast(cart) {
  if (!cart) return;

  const curr = getMembershipQualifyingSubtotal(cart);

  if (!membershipSubtotalInitialized) {
    lastKnownMembershipSubtotal = curr;
    membershipSubtotalInitialized = true;
    return;
  }

  const prev = lastKnownMembershipSubtotal;
  lastKnownMembershipSubtotal = curr;

  if (!isMembershipToastEnabled()) return;
  if (!cartHasNonMembershipItems(cart)) return;

  const min = getMembershipMinimumCents();
  if (prev < min && curr >= min) {
    showCartDrawerMembershipToast();
  }
}

function shouldShowMembershipUpsell(cart) {
  if (!cart || !Array.isArray(cart.items)) return false;
  return cartHasNonMembershipItems(cart);
}

function isCartDrawerMembershipUpsell(upsell) {
  return Boolean(upsell && upsell.id === 'cart-drawer-membership');
}

async function syncCartDrawerMembershipWithCart(cart) {
  const cartDrawer = document.getElementById('cart-drawer');
  const mount = document.getElementById('cart-drawer-membership-mount');
  const pageMount = document.getElementById('cart-page-membership-mount');

  if (isCustomerMember()) {
    hideMembershipMounts();
    evaluateMembershipThresholdToast(cart);
    return;
  }

  if (!cartDrawer && !pageMount) {
    evaluateMembershipThresholdToast(cart);
    return;
  }

  const upsell = document.getElementById('cart-drawer-membership');
  const showUpsell = shouldShowMembershipUpsell(cart);

  if (!showUpsell) {
    if (upsell) {
      const toggle = upsell.querySelector('[data-membership-toggle]');
      setMembershipToggleChecked(upsell, toggle, false);
    }
    if (mount) mount.classList.add('hidden');

    const membershipLine = cart.items.find((line) => isMembershipLine(line));
    if (membershipLine) {
      const variantId = membershipLine.variant_id || membershipLine.id
        || parseInt(upsell?.dataset?.variantId, 10)
        || parseInt(document.getElementById('cart-page-membership')?.dataset?.variantId, 10)
        || parseInt(cartDrawer?.dataset?.membershipVariantId, 10);
      if (!Number.isNaN(variantId)) {
        const toggle = upsell?.querySelector('[data-membership-toggle]');
        try {
          if (toggle) toggle.disabled = true;
          await removeMembershipFromCart(variantId);
          refreshCartAfterUpsellChange();
        } catch (error) {
          console.error('Membership auto-remove error:', error);
        } finally {
          if (toggle) toggle.disabled = false;
        }
      }
    }
    evaluateMembershipThresholdToast(cart);
    return;
  }

  const hasProducts = cartHasNonMembershipItems(cart);

  if (mount) {
    if (hasProducts) {
      mount.classList.remove('hidden');
    } else {
      mount.classList.add('hidden');
    }
  }

  if (pageMount) {
    if (hasProducts) {
      pageMount.classList.remove('hidden');
    } else {
      pageMount.classList.add('hidden');
    }
  }

  if (!hasProducts && upsell) {
    const toggle = upsell.querySelector('[data-membership-toggle]');
    const variantId = parseInt(upsell.dataset.variantId, 10);
    const membershipInCart = !Number.isNaN(variantId)
      && cart.items.some((item) => isMembershipLine(item, variantId));

    if (membershipInCart) {
      try {
        if (toggle) toggle.disabled = true;
        await removeMembershipFromCart(variantId);
        setMembershipToggleChecked(upsell, toggle, false);
        if (mount) mount.classList.add('hidden');
        refreshCartAfterUpsellChange();
      } catch (error) {
        console.error('Membership auto-remove error:', error);
      } finally {
        if (toggle) toggle.disabled = false;
      }
    } else {
      setMembershipToggleChecked(upsell, toggle, false);
      if (mount) mount.classList.add('hidden');
    }
  }

  evaluateMembershipThresholdToast(cart);
}

async function syncCartDrawerMembershipMountVisibility() {
  if (!document.getElementById('cart-drawer') && !document.getElementById('cart-page-membership-mount')) return;

  if (isCustomerMember()) {
    hideMembershipMounts();
    try {
      const cart = await fetchCartJson();
      evaluateMembershipThresholdToast(cart);
    } catch (error) {
      // ignore
    }
    return;
  }

  try {
    const cart = await fetchCartJson();
    await syncCartDrawerMembershipWithCart(cart);
    if (typeof window.syncMembershipWelcome === 'function') {
      await window.syncMembershipWelcome();
    }
  } catch (error) {
    console.error('Membership mount visibility sync error:', error);
  }
}

function scheduleMembershipBoot() {
  clearTimeout(membershipBootTimer);
  membershipBootTimer = setTimeout(async () => {
    await syncCartDrawerMembershipMountVisibility();
    bootCartDrawerMembership();
  }, 300);
}

function bootCartDrawerMembership(scope) {
  initCartDrawerMembership(scope || document.getElementById('cart-drawer') || document.getElementById('cart-page-upsells') || document);
}

function bindMembershipToggleDelegation() {
  if (document.documentElement.dataset.membershipToggleBound === 'true') return;
  document.documentElement.dataset.membershipToggleBound = 'true';

  document.addEventListener(
    'click',
    (event) => {
      const toggle = event.target.closest('[data-membership-toggle]');
      if (!toggle) return;
      event.preventDefault();
      event.stopPropagation();
      handleMembershipToggle(toggle);
    },
    true
  );
}

bindMembershipToggleDelegation();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleMembershipBoot);
} else {
  scheduleMembershipBoot();
}

document.addEventListener('dispatch:cart-drawer:refresh', () => {
  scheduleMembershipBoot();
});

document.addEventListener('on:line-item:change', (event) => {
  if (!document.getElementById('cart-drawer')) return;
  const cart = event.detail?.cart;
  if (cart) syncCartDrawerMembershipWithCart(cart);
});

document.addEventListener('on:cart:add', async () => {
  if (!document.getElementById('cart-drawer')) return;
  try {
    const cart = await fetchCartJson();
    await syncCartDrawerMembershipWithCart(cart);
  } catch (error) {
    console.error('Membership toast sync error:', error);
  }
});

async function maybeShowMembershipToastOnDrawerOpen() {
  if (!isMembershipToastEnabled()) return;

  try {
    const cart = await fetchCartJson();
    if (!cartHasNonMembershipItems(cart)) return;

    const subtotal = getMembershipQualifyingSubtotal(cart);
    if (subtotal >= getMembershipMinimumCents()) {
      showCartDrawerMembershipToast();
    }
  } catch (error) {
    console.error('Membership open toast error:', error);
  }
}

document.addEventListener('on:cart-drawer:after-open', () => {
  maybeShowMembershipToastOnDrawerOpen();
});

window.initCartDrawerMembership = initCartDrawerMembership;
